import type { Zone } from '@/lib/zones';

export interface ResultSubmissionPayload {
  date: string;
  client_id: string;
  score: number;
  shot1_zone: Zone;
  shot2_zone: Zone;
  shot3_zone: Zone;
  shot4_zone: Zone;
  shot5_zone: Zone;
  // Plan #5 §6.3: v2 7-shot payload. Optional for defensive/historical compat —
  // the worker validator (Task 14) accepts both 5-shot (v1) and 7-shot (v2) shapes.
  // Current client always submits all 7; left optional so the type does not
  // reject a hypothetical legacy caller.
  shot6_zone?: Zone;
  shot7_zone?: Zone;
}

const submittedDatesThisSession = new Set<string>();

/**
 * Best-effort POST to /api/result. Fire-and-forget — errors are swallowed
 * (logged in dev only). Dedupes within one module load via a session Set;
 * full page reload re-submits and relies on D1 ON CONFLICT DO NOTHING.
 *
 * IMPORTANT: marks the date as submitted BEFORE issuing the fetch so concurrent
 * callers within the same render cycle (e.g. two ResultScene mounts) cannot
 * race a duplicate POST.
 */
export async function submitResult(payload: ResultSubmissionPayload): Promise<void> {
  if (submittedDatesThisSession.has(payload.date)) return;
  submittedDatesThisSession.add(payload.date);
  try {
    await fetch('/api/result', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    if (typeof import.meta !== 'undefined' && (import.meta as ImportMeta & { env?: { DEV?: boolean } }).env?.DEV) {
      console.warn('submitResult failed', err);
    }
  }
}

/** Test-only: clears the session Set between tests. Not exported via index. */
export function _resetForTests(): void {
  submittedDatesThisSession.clear();
}
